import React, { useEffect, useState, useRef, useCallback } from 'react'
import { useInView } from 'react-intersection-observer'

type Props = {
  autoplay: boolean
  loop: boolean
  name: string
  className: string
  style: React.CSSProperties
  animationTimeout: number
}

function Animation({
  autoplay = false,
  loop = false,
  name,
  className,
  style,
  animationTimeout = 750,
  ...rest
}: Props) {
  const [animation, setAnimation] = useState<{ play: () => void } | null>(null)
  const animationRef = useRef(null)
  const [inViewRef, inView] = useInView({ threshold: 0.8, triggerOnce: true })

  useEffect(() => {
    let animationParams = {
      container: animationRef.current,
      renderer: 'svg',
      loop,
      autoplay,
      animationData: null,
    }

    if (!inView) return

    Promise.all([
      import(
        /* webpackChunkName: "BodyMovin" */ 'bodymovin/build/player/bodymovin_light'
      ),
      import(/* webpackChunkName: "[request]" */ `./animations/${name}`),
    ]).then(function ([bodymovin, animationData]) {
      setAnimation(
        bodymovin.loadAnimation({ ...animationParams, animationData })
      )
    })
  }, [animationRef, inView, name, autoplay, loop])

  useEffect(() => {
    if (animation) {
      setTimeout(function () {
        animation.play()
      }, animationTimeout)
    }
  }, [animation, animationTimeout])

  const setRefs = useCallback(
    (node) => {
      // Ref's from useRef needs to have the node assigned to `current`
      animationRef.current = node
      // Callback refs, like the one from `useInView`, is a function that takes the node as an argument
      inViewRef(node)
    },
    [inViewRef]
  )

  return <div className={className} ref={setRefs} style={style} {...rest} />
}

export default Animation
